import ErrorMessage from "@/v2-ui/ErrorMessage"
import Skeleton from "@/v2-ui/skeleton"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import CustomerFeatureAccessEditor from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccessEditor"
import { useCustomerFeatureAccessQuery } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.query"
import { STAFF_FEATURE_ACCESS_SENTENCE } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.utils"
import UsageLicenseDeniedCallout from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedCallout"

export type CustomerFeatureAccessProps = {
  customerId: number
  customerName: string
  /** The HubSpot id; without it the "blocked by license" callout has no Activity link and stays off. */
  externalCustomerId?: string
}

// The tab's shape while the head read is in flight: the headline's three
// lines and the editor's cards, in place, so the page does not go dark
// behind a spinner and then jump. The user panel's What they get has the
// same shape, so it shows the same skeleton.
export function CustomerFeatureAccessSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 px-3 py-2.5">
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
      <LicenseFeatureGroupComposeSkeleton withViewSwitch />
    </div>
  )
}

// The customer panel's Feature access tab: what this customer's users can
// use, said first as the three sources it comes from (the headline), then
// shown row by row through the package editor in the customer lens
// (CustomerFeatureAccessEditor). Everything decided here is per customer:
// the one place a customer's access is narrowed or widened beyond what its
// licenses carry (the per-team whitelist it succeeded retired on 26 Oct
// 2026, FUS-2787). The staff customer (Placepoint itself) gets one sentence alone: the rule
// grants its users everything and the write refuses it a closed class, so
// an editor would only offer what cannot be saved.
//
// Above the editor, when there is one: what the customer's people tried to
// use and were refused (AIP-47) — read here, where the packages that would
// unlock it are decided.
function CustomerFeatureAccess(props: CustomerFeatureAccessProps) {
  const { customerId, customerName, externalCustomerId } = props
  const { data, previousData, loading, error } = useCustomerFeatureAccessQuery({
    variables: { customerId },
  })
  const access = (data ?? previousData)?.getCustomerFeatureAccess

  if (error) return <ErrorMessage error={error} />
  if (!access) return loading ? <CustomerFeatureAccessSkeleton /> : null

  if (access.IsStaff) {
    return (
      <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <p>{STAFF_FEATURE_ACCESS_SENTENCE}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {externalCustomerId && (
        <UsageLicenseDeniedCallout
          customerId={customerId}
          externalCustomerId={externalCustomerId}
        />
      )}
      <CustomerFeatureAccessEditor
        customerName={customerName}
        access={access}
      />
    </div>
  )
}

export default CustomerFeatureAccess
